/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals console */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Spinner from '../Spinner/Spinner';
import Snackbar from '../Snackbar/Snackbar';
import Dashboard from '../Screens/Dashboard/Dashboard';
import Attributes from '../Screens/Attributes/Attributes';
import Entitytypes from '../Screens/Entitytypes/Entitytypes';
import Groups from '../Screens/Groups/Groups';
import Users from '../Screens/Users/Users';
import Importconfig from '../Screens/Importconfig/Importconfig';
import Import from '../Screens/Import/Import';
import Settings from '../Screens/Settings/Settings';
import BusinessReport from '../Screens/BusinessReport/BusinessReport';
import Attrib from '../Screens/Attrib/Attrib';
import Entity from '../Screens/Entity/Entity';
import Entitytype from '../Screens/Entitytype/Entitytype';
import Group from '../Screens/Group/Group';
import User from '../Screens/User/User';
import Help from '../Help/Help';
import $ from '../Util/Util';
import HelpIcon from './baseline_help_white_24dp.png';
import './Main.css';

/**
 * Represents the main component that is the holder of the custom screens.
 */

export default class Main extends Component {
   
    /**
     * Properties.
     */

    static propTypes = {
        context:                    PropTypes.object.isRequired,
        onLogout:                   PropTypes.func.isRequired,
        onSetActionbarTitle:        PropTypes.func.isRequired,
        onSetActionbarRightIcon:    PropTypes.func.isRequired,
        onSetBack:                  PropTypes.func.isRequired
    }

    /**
     * Default state.
     */

    state = {
        loading:        false,
        screenstack:    [],
        help:           false,
        attribute:      null,
        entityid:       null,
        entitypath:     null,
        entityreadonly: false,
        group:          null,
        user:           null
    }
 
    /**
     * Gets the name of the application.
     */

    static appname = () => {
        return 'mini CRS';
    }
    
    /**
     * Event that fires when an icon on the controlbar touched.
     * 
     * @param   {String}    icon        name of the selected icon
     */

    _iconSelected = (icon) => {

        // Showing the screen
        
        this.setState({screenstack: [icon] });
        
        // Removing the back icon from the actionbar
        
        this.props.onSetBack(false);
    }

    /**
     * Event that fires when a modal spinner has to be shown or hide.
     * 
     * @param   {Boolean}    show       show or hide the spinner
     */

    _onLoading = (show) => {
        this.setState({loading: show});
    }

    /**
     * Event that fires when a snackbar has to be shown.
     * 
     * @param   {String}     message    message that has to show
     * @param   {Boolean}    error      is it an error message or not
     */

    _onSnackbar = (message, error) => {
        this.snackbar.show(message, error);
    }    

    /**
     * Event that fires when the user selects an attribute from the attribute screen.
     * 
     * @param   {Object}    attribute    selected attribute
     */

    _onAttributeSelected = (attribute) => {
        this.setState({ attribute: attribute}, () => {
            this.showScreen('attrib', true);
        });
    }

    /**
     * Event that fires when the user selects an entity from the dashboard screen.
     * 
     * @param   {Object}    id          selected entity id
     * @param   {String}    path        path of the entity
     * @param   {Boolean}   readonly    read only state
     */

    _onEntitySelected = (id, path, readonly) => {
        this.setState({ entityid: id, entitypath: path, entityreadonly: readonly }, () => {
            this.showScreen('entity', true);
        });
    }

    /**
     * Event that fires when the user selects an entity type from the entitytypes screen.
     * 
     * @param   {Object}    entitytype        selected entity type
     */

    _onEntitytypeSelected = (entitytype) => {
        this.setState({ entitytype: entitytype }, () => {
            this.showScreen('entitytype', true);
        });
    }

    /**
     * Event that fires when the user selects a group from the groups screen.
     * 
     * @param   {Object}    group        selected group
     */

    _onGroupSelected = (group) => {
        this.setState({ group: group }, () => {
            this.showScreen('group', true);
        });
    }

    /**
     * Event that fires when the user selects a user from the users screen.
     * 
     * @param   {Object}    user        selected user
     */

    _onUserSelected = (user) => {
        this.setState({ user: user }, () => {
            this.showScreen('user', true);
        });
    }

    /**
     * Closes the help screen.
     */

    _closeHelp = () => {
        this.setState({ help: false });
    }

    /**
     * Shows the specified screen and shows the back icon on the
     * actionbar.
     * 
     * @param   {Object}    screen          screen that has to show
     * @param   {Boolean}   popup           is it a popup screen. Default: false
     */

    showScreen = (screen, popup) => {

        // Adding popup screen to the stack

        if (popup) {
            let screenstack = this.state.screenstack;
            screenstack.push(screen);        
            this.setState({ screenstack: screenstack });

            // Setting the back icon on the actionbar

            this.props.onSetBack(true);
        } else {

            // Clearing the screen stack and adding the requested screen to it

            this.setState({ screenstack: [screen] });

            // Setting the back icon on the actionbar

            this.props.onSetBack(false);
        }
    }

    /**
     * Event that fires when the user touches the back icon on the
     * actionbar.
     */

    onBack = () => {

        // Remove the last screen from the stack

        let screenstack = this.state.screenstack;
        screenstack.splice(-1, 1);
        this.setState({ screenstack: screenstack });

        // Setting the back icon on the actionbar

        if (screenstack.length === 1) {
            this.props.onSetBack(false);
        }
    }

    /** 
     * Event that fires when the user touches the right icon on the
     * actionbar.
     */

    onRightIconTouched = () => {

        // Shows the help screen

        this.setState({ help: true });
    }

    /**
     * CTRL+[key] handler.
     * 
     * @param   {Number}    key     code of the pressed key
     */

    controlKeyHandler = (key) => {

        // Shows the help screen

        if (key === 112) { // CTRL+F1
            this.setState({ help: !this.state.help });
        }
    }

    /** 
     * Event that fires when the component mounted.
     */

    componentDidMount() {

        // Setting the help icon

        this.props.onSetActionbarRightIcon({ 
            icon:       HelpIcon, 
            tooltip:    this.props.context.locale.help.icon_tooltip
        });

        // Listening to the "snackbar" event

        $.listenEvent('snackbar', (data) => {
            this._onSnackbar(data.message, data.error);
        });  
    }    

    /** 
     * Main renderer.
     */

    render() {
        return (
            <div className="main vertical">
                <div className="flex">

                    { /* Dashboard screen */}

                    { this.state.screenstack[this.state.screenstack.length - 1] === 'dashboard' && this.props.context.user &&
                        <Dashboard context={this.props.context} onLoading={this._onLoading} onSetActionbarTitle={this.props.onSetActionbarTitle} onEntitySelected={this._onEntitySelected} />
                    }

                    { /* Attribute maintenance screen */}

                    { this.state.screenstack[this.state.screenstack.length - 1] === 'attributes' && this.props.context.user &&
                        <Attributes context={this.props.context} onLoading={this._onLoading} onSetActionbarTitle={this.props.onSetActionbarTitle} onAttributeSelected={this._onAttributeSelected} />
                    }

                    { /* Entity type maintenance screen */}

                    { this.state.screenstack[this.state.screenstack.length - 1] === 'entitytypes' && this.props.context.user &&
                        <Entitytypes context={this.props.context} onLoading={this._onLoading} onSetActionbarTitle={this.props.onSetActionbarTitle} onEntitytypeSelected={this._onEntitytypeSelected}/>
                    }

                    { /* Group maintenance screen */}

                    { this.state.screenstack[this.state.screenstack.length - 1] === 'groups' && this.props.context.user &&
                        <Groups context={this.props.context} onLoading={this._onLoading} onSetActionbarTitle={this.props.onSetActionbarTitle} onGroupSelected={this._onGroupSelected} />
                    }

                    { /* User maintenance screen */}

                    { this.state.screenstack[this.state.screenstack.length - 1] === 'users' && this.props.context.user &&
                        <Users context={this.props.context} onLoading={this._onLoading} onSetActionbarTitle={this.props.onSetActionbarTitle} onUserSelected={this._onUserSelected} />
                    }

                    { /* Import configuration screen */}

                    { this.state.screenstack[this.state.screenstack.length - 1] === 'importconfig' && this.props.context.user &&
                        <Importconfig context={this.props.context} onLoading={this._onLoading} onSetActionbarTitle={this.props.onSetActionbarTitle} />
                    }

                    { /* Import screen */}

                    { this.state.screenstack[this.state.screenstack.length - 1] === 'import' && this.props.context.user &&
                        <Import context={this.props.context} onLoading={this._onLoading} onSetActionbarTitle={this.props.onSetActionbarTitle} />
                    }

                    { /* Settings screen */}

                    { this.state.screenstack[this.state.screenstack.length - 1] === 'settings' && this.props.context.user &&
                        <Settings context={this.props.context} onLoading={this._onLoading} onSetActionbarTitle={this.props.onSetActionbarTitle} />
                    }

                    { /* An attribute maintenance screen */}

                    { this.props.context.environment.browser.info.mobile && this.state.screenstack[this.state.screenstack.length - 1] === 'attrib' && this.props.context.user &&
                        <Attrib context={this.props.context} attribute={this.state.attribute} onLoading={this._onLoading} onSetActionbarTitle={this.props.onSetActionbarTitle} onBack={this.onBack} />
                    }

                    { /* Entity maintenance screen */}

                    { this.props.context.environment.browser.info.mobile && this.state.screenstack[this.state.screenstack.length - 1] === 'entity' && this.props.context.user &&
                        <Entity context={this.props.context} id={this.state.entityid} readonly={this.state.entityreadonly} path={this.state.entitypath} onLoading={this._onLoading} onSetActionbarTitle={this.props.onSetActionbarTitle} onBack={this.onBack} />
                    }

                    { /* Entity type maintenance screen */}

                    { this.props.context.environment.browser.info.mobile && this.state.screenstack[this.state.screenstack.length - 1] === 'entitytype' && this.props.context.user &&
                        <Entitytype context={this.props.context} entitytype={this.state.entitytype} onLoading={this._onLoading} onSetActionbarTitle={this.props.onSetActionbarTitle} onBack={this.onBack} />
                    }

                    { /* Group maintenance screen */}

                    { this.props.context.environment.browser.info.mobile && this.state.screenstack[this.state.screenstack.length - 1] === 'group' && this.props.context.user &&
                        <Group context={this.props.context} group={this.state.group} onLoading={this._onLoading} onSetActionbarTitle={this.props.onSetActionbarTitle} onBack={this.onBack} />
                    }

                    { /* User maintenance screen */}

                    { this.props.context.environment.browser.info.mobile && this.state.screenstack[this.state.screenstack.length - 1] === 'user' && this.props.context.user &&
                        <User context={this.props.context} user={this.state.user} onLoading={this._onLoading} onSetActionbarTitle={this.props.onSetActionbarTitle} onBack={this.onBack} />
                    }

                    { /* Business report screen */}

                    { this.state.screenstack[this.state.screenstack.length - 1] === 'businessreport' && this.props.context.user &&
                        <BusinessReport context={this.props.context} onLoading={this._onLoading} onSetActionbarTitle={this.props.onSetActionbarTitle} />
                    }
                </div>
                
                { /* Spinner */}

                { this.state.loading && <Spinner modal /> }

                {/* Snackbar */}

                <Snackbar ref={c => {this.snackbar = c;}} />

                { /* Help */}

                { this.state.help && <Help context={this.props.context} screen={this.state.screenstack[this.state.screenstack.length - 1]} onCloseHelp={this._closeHelp} /> }
            </div>        
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Main/Main.js